import { ChartPoint } from 'components/metric/chart';
import { AssocArray, YNValues } from 'tools/types';
import { FormComponentValue } from 'components/common/form/layout/control';
import { ReportContentActions } from 'app/editor/report';
import { GroupRule } from 'components/dataset-viewer';
import { ReportFilterSegmentValuesData } from 'components/report-content/hooks/useReportContentSettings';
import { ComponentSettingsManageFilters } from './cmp/manage-filters';
import { ChartAxis } from 'components/chart';
import { BookmarkType } from 'components/external-reference/bookmark/BookmarkDropdown';

export type OrderDirection = 'ASC' | 'DESC';
export type DatasetFieldValueType = 'text' | 'numeric' | 'datetime';
export type TextAlignType = 'left' | 'center' | 'right';

export type AllowedComponentSectionType = 'chart' | 'table' | 'other';
export type ContentMode = 'view' | 'edit';

export interface ManageFiltersInstance {
    dataset_instance_id: number;
    measurement_time: string;
    measurement_time_raw: string;
    rows_number: number;
}

export interface ContentSettings {
    elementId: number;
    segmentValueId: number;
    context: ContentMode;
    pageSize: number;
    applyAccessMap: YNValues;
    instances: Array<ManageFiltersInstance>;
    hasInstances: boolean;
    segmentValues: ReportFilterSegmentValuesData;
    measurement_time?: string;
    forScreenShot: boolean;
    displayMasks: Array<DisplayMask>;
    related: Array<FormComponentValue>;
    bookmarks: Array<BookmarkType>;
    // Used to map segment value to bookmark filter
    segmentData: Array<any>;
}

// Used for data requests, can be extended with additional properties as needed for specific components
export interface IComponentProperties {
    elementId: number;
    segmentValueId: number;
    context: ContentMode;
    pageSize: number;
    applyAccessMap: YNValues;
    forScreenShot: boolean;
    measurement_time?: string;
}
export interface ComponentSettings {
    title: string;
    height: number;
    show_in_viewer: YNValues;
    is_preview_ind: YNValues;
    include_in_email: YNValues;
    include_in_email_attachment: YNValues;
    isOnCreation?: boolean;
    internal_name: ComponentsType;
    is_collapsed_ind: YNValues;
    groupFilter: GroupRule;
    hasNoData?: boolean;
}

export type ComponentsType =
    | 'manage_filters'
    | 'bubble_chart'
    | 'dataset'
    | 'chart'
    | 'external_visualization'
    | 'funnel_chart'
    | 'jct'
    | 'kpi'
    | 'kpi_chart'
    | 'line'
    | 'map_chart'
    | 'pivot'
    | 'range_chart'
    | 'text_block'
    | 'llm';

export interface AllowedComponentType {
    datasetRendererComponentId: number;
    // datasetReportDescription: null;
    displayOrder: number;
    faIconName?: string;
    iconInd?: string;
    image: string;
    internalName: ComponentsType;
    isEnabledInd: true;
    isSystemInd: true;
    // javascriptChartTemplateId: null;
    name: string;
    section: AllowedComponentSectionType;
    settings: any;
    properties?: any;
}

export interface BlockComponent<T extends ComponentSettings> {
    datasetRendererBlockComponentId: number;
    datasetRendererBlockId: number;
    datasetRendererComponentId: number;
    displayOrder: number;
    internalName: ComponentsType;
    // javascriptChartTemplateId: null;
    settings: T;
}

export interface BlockType<T extends ComponentSettings> {
    component: BlockComponent<T>;
    // datasetRendererBlockId: number;
    displayOrder: number;
    // elementId: number;
    uid: string;
}

export interface ComponentProps<T extends ComponentSettings> {
    component: BlockComponent<T>;
    contentSettings: ContentSettings;
    actions: ReportContentActions;
    editPanel?: any;
    blockId?: string;
}

export interface ComponentUpdateProps<T extends ComponentSettings> extends ComponentProps<T> {
    updateBlockSettings: (newSettings: T, options?: { shouldDirty?: boolean }) => void;
}

export interface DatasetField {
    // aggregate_function: '';
    // column_instance: 'current';
    column_name: string;
    column_sequence: number;
    dataset_column_id: number;
    dataset_filter_column_id: number;
    dataset_filter_id: number;
    dataset_id: number;
    display_mask_id: string | null;
    // is_derived_ind: 'N';
    // is_result_ind: 'Y';
    name: string;
    reference_name: string;
    reference_name_escaped: string;
    value_type: DatasetFieldValueType;
    label?: string;
}

export type RendererFilterType = 'dropdown' | 'charting_interval' | 'range' | 'datetime' | 'multiselect';

export interface RendererFilter extends DatasetField {
    filter_column: YNValues;
    include_all_value?: YNValues;
    filterType?: RendererFilterType;
    filter_label?: string;
    sortDirection: OrderDirection;
    show_date_only?: YNValues;
    column_sequence: number;
    savedValues?: Array<any>;
    numeric_type: 'integer' | 'float' | null;
    label?: string;
    reference_name_escaped: string;
    use_external_filter_value?: YNValues;
    external_dataset_id?: number | null;
    external_dataset_column_value_id?: string | null;
    external_dataset_column_value_label?: string | null;
    external_dataset_column_title_id?: string | null;
    external_dataset_column_title_label?: string | null;
    external_delimiter?: string | null;
}

export interface ViewerData {
    rendererBlocks: Array<BlockType<ComponentSettings>>;
    rendererFilters: ComponentSettingsManageFilters;
    datasetFields: Array<DatasetField>;
    bookmarksList: Array<BookmarkType>;
    segmentData: Array<any>;
}

export interface EditorData extends ViewerData {
    allowedComponents: AssocArray<AllowedComponentType>;
    displayMasks: Array<DisplayMask>;
}

export interface MeasureField {
    aggregation_function: string;
    column_name: string;
    reference_name: string;
    value_type: DatasetFieldValueType;
}

export interface SimpleFormattingRule {
    condition: string;
    data: string;
    bgColor?: string;
    data_second?: string;
    textBold?: boolean;
    textColor?: string;
    textItalic?: boolean;
}

export interface IFormattingRuleConditions {
    key: string;
    name: string;
    value_type: 'numeric' | 'text';
}
export const FormattingRuleConditions: Array<IFormattingRuleConditions> = [
    { key: '>', name: 'label_greater_than', value_type: 'numeric' },
    { key: '>=', name: 'label_greater_than_or_equal', value_type: 'numeric' },
    { key: '=', name: 'label_equals', value_type: 'numeric' },
    { key: '!=', name: 'label_does_not_equal', value_type: 'numeric' },
    { key: '<', name: 'label_less_than', value_type: 'numeric' },
    { key: '<=', name: 'label_less_than_or_equal', value_type: 'numeric' },
    { key: 'between', name: 'label_between', value_type: 'numeric' },
    { key: '=', name: 'label_exactly_matches', value_type: 'text' },
    { key: '!=', name: 'label_does_not_equal', value_type: 'text' },
    { key: 'contains', name: 'label_contains', value_type: 'text' },
    { key: 'does not contain', name: 'label_does_not_contain', value_type: 'text' },
];

export const AggregationMethods: Array<FormComponentValue> = [
    { value: 'Sum', label: 'label_sum' },
    { value: 'Min', label: 'label_min' },
    { value: 'Max', label: 'label_max' },
    { value: 'Avg', label: 'label_avg' },
    { value: 'Count', label: 'label_count' },
    { value: 'Count Distinct', label: 'label_count_distinct' },
];

export interface DisplayMask {
    display_mask_id: string;
    display_example: string;
    mask_description: string;
    currency_mask_ind?: YNValues;
    // currency_id: null;
    date_format_string: string;
    data_format_string_js: string;
    // decimal_point_delimiter: '.';
    // default_float_mask_ind: 'N';
    // default_int_mask_ind: 'N';
    // display_precision_digits: 1;
    // last_updated_by: null;
    // last_updated_time: '2016-08-26 23:04:52';
    mask_type: 'numeric' | 'date';
    // prefix: null;
    // show_amount_as: 'Raw Number';
    // show_calendar_quarter_ind: null;
    // suffix: '%';
    // thousands_delimiter: null;
}

export interface DrillTo {
    // index: number;
    elementId: number;
    elementTitle: string;
    columnsMap: Array<{
        field: string;
        alias: string;
    }>;
}

export interface DrillToField {
    reference_name: string;
    column_name: string;
}

export interface ReportChartPoint extends ChartPoint {
    key: { y_f: number; name_f: string; x_f?: string };
}

export interface ComponentSettingsChartMeasureField extends MeasureField {
    uId: string;
    // aggregate_function: '';
    // aggregation: false;
    color: string;
    // column_instance: 'current';
    // column_sequence: 3;
    dataset_column_id: number | null;
    dataset_filter_column_id: number | null;
    dataset_filter_id: number | null;
    dataset_id: number | null;
    display_mask_id: string;
    display_type?: string;
    // field: 'sales';
    // field_type: 'measure';
    // id: 'sales';
    // instance: 'current';
    // is_derived_ind: 'N';
    // is_result_ind: 'Y';
    // key: 'sales-current';
    label: string;
    // label_new: 'sales';
    // name: 'sales';
    // ref_column_name: 'sales';
    reference_name_escaped: string;
    y_axis: ChartAxis;
}
